import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0A1128]/95 backdrop-blur-md border-b border-cyan-500/30' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-shield-check-line text-3xl text-cyan-400"></i>
            </div>
            <span className="text-white text-xl font-bold tracking-tight" style={{ fontFamily: 'Orbitron, sans-serif' }}>ITALMACERI SRL</span>
          </div>
          <div className="flex items-center gap-8">
            <a href="#home" className="text-white text-sm font-medium tracking-widest uppercase hover:text-cyan-400 transition-colors cursor-pointer">HOME</a>
            <a href="#features" className="text-white text-sm font-medium tracking-widest uppercase hover:text-cyan-400 transition-colors cursor-pointer">FEATURES</a>
            <a href="#about" className="text-white text-sm font-medium tracking-widest uppercase hover:text-cyan-400 transition-colors cursor-pointer">ABOUT</a>
            <a href="#contact" className="text-white text-sm font-medium tracking-widest uppercase hover:text-cyan-400 transition-colors cursor-pointer">CONTACT</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center bg-gradient-to-br from-[#0A1128] via-[#1a1d4a] to-[#2d1b4e] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-64 h-64 bg-cyan-500 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-600 rounded-full blur-[150px]"></div>
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-6 py-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 rounded-full border border-cyan-400/50 bg-cyan-400/10">
                <span className="text-cyan-400 text-xs font-semibold tracking-wider uppercase">ENTERPRISE-GRADE SECURITY</span>
              </div>
              
              <h1 className="text-7xl font-thin text-white leading-tight" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Secure Your<br />
                <span className="font-light">Digital</span><br />
                <span className="font-normal">Presence</span>
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed max-w-xl">
                Military-grade encryption technology protecting your online privacy across all devices
              </p>
              
              <div className="flex flex-col gap-4">
                <button className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 flex items-center gap-3 w-fit whitespace-nowrap cursor-pointer">
                  GET STARTED
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </button>
                
                <div className="flex items-center gap-2 text-gray-400">
                  <i className="ri-mail-line text-lg"></i>
                  <span className="text-sm">contact@italmacerisrl.com</span>
                </div>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative h-[600px] flex items-center justify-center">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-full h-full">
                  {/* Floating geometric shapes */}
                  <div className="absolute top-1/4 left-1/4 w-32 h-32 border-2 border-cyan-400/30 rounded-lg animate-[spin_20s_linear_infinite]"></div>
                  <div className="absolute top-1/3 right-1/4 w-24 h-24 border-2 border-purple-400/30 rounded-full animate-[spin_15s_linear_infinite_reverse]"></div>
                  <div className="absolute bottom-1/3 left-1/3 w-20 h-20 border-2 border-cyan-400/40 rotate-45 animate-[spin_25s_linear_infinite]"></div>
                  
                  {/* Central shield */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 flex items-center justify-center">
                    <div className="relative w-full h-full">
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-purple-600/20 rounded-full blur-3xl animate-pulse"></div>
                      <i className="ri-shield-check-fill text-[200px] text-cyan-400/40 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></i>
                    </div>
                  </div>
                  
                  {/* Particle effects */}
                  <div className="absolute top-1/4 right-1/3 w-2 h-2 bg-cyan-400 rounded-full animate-ping"></div>
                  <div className="absolute bottom-1/4 left-1/4 w-2 h-2 bg-purple-400 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
                  <div className="absolute top-2/3 right-1/4 w-2 h-2 bg-cyan-400 rounded-full animate-ping" style={{ animationDelay: '2s' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-[#F8F9FA]">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0A1128]/20">
              <i className="ri-lock-line text-[#0A1128]"></i>
              <span className="text-[#0A1128] text-sm font-semibold tracking-wider uppercase">ADVANCED PROTECTION</span>
            </div>
            <h2 className="text-5xl font-bold text-[#0A1128]">Why Choose ITALMACERI VPN</h2>
            <div className="w-20 h-1 bg-cyan-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Feature 1 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 space-y-6 group cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 mx-auto group-hover:scale-110 transition-transform">
                <i className="ri-shield-check-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#0A1128] text-center">End-to-End Encryption</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Military-grade AES-256 encryption ensures your data remains completely secure and private from any third-party access or surveillance attempts.
              </p>
              <div className="w-10 h-1 bg-cyan-500"></div>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 space-y-6 group cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 mx-auto group-hover:scale-110 transition-transform">
                <i className="ri-global-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#0A1128] text-center">Global Server Network</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Access our worldwide network of high-speed servers strategically located across multiple continents for optimal performance and reliability.
              </p>
              <div className="w-10 h-1 bg-cyan-500"></div>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 space-y-6 group cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 mx-auto group-hover:scale-110 transition-transform">
                <i className="ri-file-shield-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#0A1128] text-center">Zero-Log Policy</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                We maintain a strict no-logging policy, ensuring your browsing history, connection data, and personal information are never stored or tracked.
              </p>
              <div className="w-10 h-1 bg-cyan-500"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-center">
            {/* Left Icons */}
            <div className="lg:col-span-2 space-y-12">
              <div className="flex items-center justify-center">
                <div className="w-32 h-32 flex items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400/20 to-cyan-600/20">
                  <i className="ri-server-line text-6xl text-cyan-600"></i>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <div className="w-32 h-32 flex items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400/20 to-cyan-600/20">
                  <i className="ri-node-tree text-6xl text-cyan-600"></i>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <div className="w-32 h-32 flex items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400/20 to-cyan-600/20">
                  <i className="ri-shield-star-line text-6xl text-cyan-600"></i>
                </div>
              </div>
            </div>

            {/* Right Content */}
            <div className="lg:col-span-3 space-y-8">
              <div className="inline-block px-4 py-2 rounded-full border-2 border-[#0A1128]">
                <span className="text-[#0A1128] text-sm font-semibold tracking-wider uppercase">ABOUT ITALMACERI SRL</span>
              </div>
              
              <h2 className="text-5xl font-bold text-[#0A1128] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Italian Excellence<br />
                in Digital<br />
                Security
              </h2>
              
              <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
                <p>
                  Based in the heart of Florence, Italy, ITALMACERI SRL represents the perfect fusion of Italian craftsmanship and cutting-edge cybersecurity technology. Our mission is to provide individuals and businesses with uncompromising digital privacy protection.
                </p>
                <p>
                  We believe that online privacy is a fundamental right, not a luxury. That's why we've developed our VPN solution with the highest standards of encryption and security protocols, ensuring your digital freedom remains protected at all times.
                </p>
                <p>
                  Our team of security experts continuously monitors and updates our infrastructure to stay ahead of emerging threats, providing you with peace of mind in an increasingly connected world.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#0A1128] via-[#1a1d4a] to-[#0A1128] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500 rounded-full blur-[150px]"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600 rounded-full blur-[150px]"></div>
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-6 text-center space-y-8">
          <h2 className="text-5xl font-bold text-white">Ready to Protect Your Privacy?</h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Join thousands of users securing their digital life with ITALMACERI VPN
          </p>
          
          <button className="group px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-lg font-semibold rounded-xl hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 flex items-center gap-3 mx-auto whitespace-nowrap cursor-pointer">
            <i className="ri-download-line text-2xl"></i>
            DOWNLOAD NOW
            <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
          </button>
        </div>

        {/* Bottom Visual Banner */}
        <div className="absolute bottom-0 left-0 right-0 h-32 opacity-20">
          <div className="absolute inset-0 flex items-center justify-center">
            <i className="ri-global-line text-[200px] text-cyan-400"></i>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#1A1D29] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, cyan 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Brand Column */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-shield-check-line text-3xl text-cyan-400"></i>
                </div>
                <span className="text-white text-xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>ITALMACERI SRL</span>
              </div>
              <p className="text-gray-400 text-sm">Securing Digital Freedom</p>
              <div className="flex gap-2">
                <input 
                  type="email" 
                  placeholder="Your email" 
                  className="flex-1 px-4 py-2 bg-transparent border border-gray-600 rounded-lg text-sm text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none transition-colors"
                />
                <button className="w-10 h-10 flex items-center justify-center bg-cyan-500 rounded-lg hover:bg-cyan-600 transition-colors cursor-pointer">
                  <i className="ri-notification-line text-white"></i>
                </button>
              </div>
            </div>

            {/* Product Column */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-sm tracking-wider uppercase">PRODUCT</h3>
              <div className="space-y-3">
                <a href="#features" className="block text-gray-400 text-sm hover:text-cyan-400 transition-colors cursor-pointer">VPN Features</a>
                <a href="#about" className="block text-gray-400 text-sm hover:text-cyan-400 transition-colors cursor-pointer">Security</a>
                <a href="#features" className="block text-gray-400 text-sm hover:text-cyan-400 transition-colors cursor-pointer">Technology</a>
              </div>
            </div>

            {/* Company Column */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-sm tracking-wider uppercase">COMPANY</h3>
              <div className="space-y-3">
                <a href="#about" className="block text-gray-400 text-sm hover:text-cyan-400 transition-colors cursor-pointer">About Us</a>
                <a href="#contact" className="block text-gray-400 text-sm hover:text-cyan-400 transition-colors cursor-pointer">Contact</a>
                <a href="#" className="block text-gray-400 text-sm hover:text-cyan-400 transition-colors cursor-pointer">Privacy Policy</a>
              </div>
            </div>

            {/* Contact Column */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-sm tracking-wider uppercase">CONTACT</h3>
              <div className="space-y-3 text-gray-400 text-sm">
                <p>VIA SACCO E VANZETTI 13<br />50145 FIRENZE<br />Italy</p>
                <div className="flex items-center gap-2">
                  <i className="ri-phone-line"></i>
                  <span>+1 9015992475</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-mail-line"></i>
                  <span>contact@italmacerisrl.com</span>
                </div>
              </div>
            </div>
          </div>

          {/* Copyright Bar */}
          <div className="pt-8 border-t border-gray-700 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">© 2025 ITALMACERI SRL. All rights reserved.</p>
            
            <div className="flex items-center gap-4">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-gray-600 rounded-lg hover:border-cyan-400 hover:text-cyan-400 transition-colors cursor-pointer">
                <i className="ri-linkedin-line text-lg"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-gray-600 rounded-lg hover:border-cyan-400 hover:text-cyan-400 transition-colors cursor-pointer">
                <i className="ri-twitter-x-line text-lg"></i>
              </a>
              <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-gray-600 rounded-lg hover:border-cyan-400 hover:text-cyan-400 transition-colors cursor-pointer">
                <i className="ri-github-line text-lg"></i>
              </a>
            </div>

            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-500 text-sm hover:text-cyan-400 transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}